import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { AppProvider } from "./app_context";
import { UserDataProvider } from "./user_data_context";
import "./i18n";
import { CometChatAuthProvider } from "./cometchat_auth_context";
import { CometChatUnreadProvider } from "./cometchat_unread_context";
import { BibleHistoryProvider } from "./bible_history_context";
import { TooltipProvider } from "./common/components/tooltip";
import { ToastNotification } from "./common/components/toast_notification";
import { getStoredFcmToken } from "./push/token_store";
import { checkEnvContract, warnAboutBrokenFeatures } from "./common/utils/env_contract";
import EnvContractScreen from "./common/components/env_contract_screen";

// Offline caching (vite-plugin-pwa) is retired — see public/sw.js. The one
// worker this app still wants is the push-only one, and only for someone who
// has already opted in on this device: registering it unconditionally for
// every guest would put a worker back on origins that never asked for one.
// `enable_push.jsx` still registers it lazily for a first-time opt-in; this
// covers a returning user whose worker `public/sw.js` just tore down.
if ("serviceWorker" in navigator && getStoredFcmToken()) {
	navigator.serviceWorker.register("/firebase-messaging-sw.js");
}

const root = ReactDOM.createRoot(document.getElementById("root"));

// Before anything reads a setting: say plainly what is missing, rather than letting the absence
// resurface three screens away as somebody else's bug. See common/utils/env_contract.ts.
const envReport = checkEnvContract(import.meta.env);

if (envReport.missingRequired.length > 0) {
	console.error(`[env] Missing required build settings: ${envReport.missingRequired.join(", ")}`);
	root.render(<EnvContractScreen missing={envReport.missingRequired} />);
} else {
	warnAboutBrokenFeatures(envReport.brokenFeatures);
	renderApp();
}

function renderApp() {
	root.render(
		<BrowserRouter>
			<UserDataProvider>
				<AppProvider>
					<BibleHistoryProvider>
						<CometChatAuthProvider>
							<CometChatUnreadProvider>
								<TooltipProvider>
									<App />
									{/* One container for the whole app: it draws into a portal on
									    <body>, and react-toastify 9 delivers each message to every
									    mounted container, so a second one shows the same toast twice. */}
									<ToastNotification />
								</TooltipProvider>
							</CometChatUnreadProvider>
						</CometChatAuthProvider>
					</BibleHistoryProvider>
				</AppProvider>
			</UserDataProvider>
		</BrowserRouter>,
	);
}
